import axios from './axiosInstance';
import { FormResponse } from '../../models/form/FormResponse';
import { APIFormResponse } from '../../types';

interface SportAreaParams {
  sport: string;
  areaId: string;
}

interface GetFormResponsesParams extends SportAreaParams {
  day?: number;
  startDate?: string;
  endDate?: string;
  featureId?: string;
  trailId?: string;
}

interface GetFormResponsesExportParams extends SportAreaParams {
  formResponseId?: string;
  day?: number;
  startDate?: string;
  endDate?: string;
  formTypes?: string | string[];
  featureId?: string;
  trailId?: string;
  spatialFeatureId?: string;
}

interface GetFormResponseParams extends SportAreaParams {
  formResponseId: string;
}

interface GetCsvParams extends SportAreaParams {
  formResponseId: string;
  fileName: string;
}

interface FormResponseMutationParams extends SportAreaParams {
  formResponse: any;
}

interface MutationOptions {
  requestId?: string | null;
}

interface PutFormResponseImageParams extends SportAreaParams {
  formResponseId: string;
  questionSetIndex: number;
  questionIndex: number;
  file: File;
}

interface DeleteFormResponseParams extends SportAreaParams {
  formResponseId: string;
}

interface GetFormResponsesCountParams extends SportAreaParams {
  day?: number;
  startDate?: string;
  endDate?: string;
  featureId?: string;
  trailId?: string;
}

const getFormResponses = ({sport, areaId, day, startDate, endDate, featureId, trailId}: GetFormResponsesParams): Promise<any> => {
  let url = `/${sport}/areas/${areaId}/trailissueformresponses`;
  // add query parameters
  const queryParameters: string[] = [];
  if (day) {
    queryParameters.push(`day=${day}`);
  } else if (startDate && endDate) {
    queryParameters.push(`startDate=${startDate}`);
    queryParameters.push(`endDate=${endDate}`);
  }
  if (featureId) {
    queryParameters.push(`featureId=${featureId}`);
  }
  if (trailId) {
    queryParameters.push(`trailId=${trailId}`);
  }
  if (queryParameters && queryParameters.length) {
    url += `?${queryParameters.join('&')}`;
  }
  return axios.get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
    });
};

const getFormResponsesExport = ({sport, areaId, formResponseId, day, startDate, endDate, formTypes, featureId, trailId, spatialFeatureId}: GetFormResponsesExportParams): Promise<any> => {
  let url = `/${sport}/areas/${areaId}/trailissueformresponses/export`;
  // add query parameters
  const queryParams: Record<string, string> = {};
  if (formResponseId) {
    queryParams.id = formResponseId;
  }
  if (day) {
    queryParams.day = String(day);
  } else if (startDate && endDate) {
    queryParams.startDate = startDate;
    queryParams.endDate = endDate;
  }
  if (formTypes) {
    queryParams.formTypes = Array.isArray(formTypes) ? formTypes.join(',') : formTypes;
  }
  if (featureId) {
    queryParams.featureId = featureId;
  }
  if (trailId) {
    queryParams.trailId = trailId;
  }
  if (spatialFeatureId) {
    queryParams.spatialFeatureId = spatialFeatureId;
  }
  return axios.get(url, {
    params: queryParams,
  })
    .then((response) => {
      const responseBody = response.data;
      return responseBody;
    });
};

const getFormResponse = ({ sport, areaId, formResponseId }: GetFormResponseParams): Promise<APIFormResponse | void> => {
  let url = `/${sport}/areas/${areaId}/trailissueformresponses/${formResponseId}`;
  return axios.get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      // The page tells "no access" (403) from "not found" (404) from a failure.
      throw error;
    });
};

const getFormResponseExport = ({ sport, areaId, formResponseId }: GetFormResponseParams): Promise<void> => {
  let url = `/${sport}/areas/${areaId}/trailissueformresponses/${formResponseId}/export`;
  return axios.get(url, {
    responseType: 'arraybuffer',
    headers: {
      'Accept': 'application/zip'
    }
  })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ullr-export.zip`);
      document.body.appendChild(link);
      link.click();
    })
    .catch((error) => {
      console.log(error);
    });
};

const getCsv = ({ sport, areaId, formResponseId, fileName }: GetCsvParams): Promise<void> => {
  let url = `/${sport}/areas/${areaId}/trailissueformresponses/${formResponseId}/csv`;
  return axios.get(url)
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}.csv`); //or any other extension
      document.body.appendChild(link);
      link.click();
    })
    .catch((error) => {
      console.log(error);
    });
};

const putFormResponse = ({sport, areaId, formResponse}: FormResponseMutationParams, {requestId}: MutationOptions): Promise<any> => {
  /**
   * Copy the completed form so that we can manipulate it without modifying the original.
  */
  const completedForm = formResponse.completedForm;
  const completedFormCopy = JSON.parse(JSON.stringify(completedForm));

  /**
   * Remove the value for any question with questionType === 'images'.
   * Because these values are files, they'll get uploaded in separate requests.
   */
  for (const questionSet of completedFormCopy.questionSets) {
    for (const question of questionSet.questions) {
      if (question.questionType === 'images') {
        question.value = null;
      }
    }
  }

  const url = `/${sport}/areas/${areaId}/trailissueformresponses`;
  return axios.put(url, {
    ...formResponse,
    completedForm: completedFormCopy,
  }, {
    headers: {
      ...(requestId ? { 'x-request-id': requestId } : {}),
    }
  })
    .then((response) => {
      const savedFormResponse = response.data;
      return FormResponse.factory(savedFormResponse);
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const putFormResponseImage = async (
  { sport, areaId, formResponseId, questionSetIndex, questionIndex, file }: PutFormResponseImageParams,
  { requestId }: MutationOptions,
): Promise<void> => {
  const base64Data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve((reader.result as string).split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const url = `/${sport}/areas/${areaId}/trailissueformresponses/${formResponseId}/images`;
  return axios.put(url, {
    questionSetIndex,
    questionIndex,
    base64File: base64Data,
  }, {
    headers: {
      ...(requestId ? { 'x-request-id': requestId } : {}),
    }
  })
    .then(() => {})
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const orchestrateUpload = async ({ sport, areaId, formResponse }: FormResponseMutationParams): Promise<APIFormResponse | void> => {
  /**
   * Determine whether there are any `images` questions in this form response.
   * If there are, they must be uploaded separately in subsequent requests
   */
  const imageUploads: PutFormResponseImageParams[] = [];
  const completedForm = formResponse.completedForm;
  // iterate through questionSets and questions
  for (let qsIndex = 0; qsIndex < completedForm.questionSets.length; qsIndex++) {
    const questionSet = completedForm.questionSets[qsIndex];
    for (let qIndex = 0; qIndex < questionSet.questions.length; qIndex++) {
      const question = questionSet.questions[qIndex];
      console.log(question.questionType)
      // if the question is of the type 'images'
      if (question.questionType === 'images') {
        const value = question.value;

        // if the value of the question is a non-empty array
        if (value && value.length) {
          // iterate over each element of the value array. each element represents an image
          for (const file of value) {
            imageUploads.push({
              sport,
              areaId,
              formResponseId: formResponse.id,
              questionSetIndex: qsIndex,
              questionIndex: qIndex,
              file: file,
            });
          }
        }
      }
    }
  }

  /**
   * Submit the form response
   */
  const savedFormResponse = await putFormResponse(
    {
      sport,
      areaId,
      formResponse,
    },
    { requestId: formResponse.id },
  );

  /**
   * Upload each image
   */
  for (let imageIndex = 0; imageIndex < imageUploads.length; imageIndex++) {
    const imageUpload = imageUploads[imageIndex];
    await putFormResponseImage(
      {
        ...imageUpload,
        formResponseId: savedFormResponse.id,
      },
      { requestId: `${imageUpload.formResponseId}-${imageUpload.questionSetIndex}-${imageUpload.questionIndex}-${imageIndex}` },
    );
  }

  return getFormResponse({
    sport,
    areaId,
    formResponseId: savedFormResponse.id,
  });
};

const deleteFormResponse = ({sport, areaId, formResponseId}: DeleteFormResponseParams): Promise<any> => {
  let url = `/${sport}/areas/${areaId}/trailissueformresponses/${formResponseId}`;
  return axios.delete(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
}

const getFormResponsesCount = ({sport, areaId, day, startDate, endDate, featureId, trailId}: GetFormResponsesCountParams): Promise<any> => {
  let url = `/${sport}/areas/${areaId}/trailissueformresponses/count`;
  // add query parameters
  const queryParameters: string[] = [];
  if (day) {
    queryParameters.push(`day=${day}`);
  } else if (startDate && endDate) {
    queryParameters.push(`startDate=${startDate}`);
    queryParameters.push(`endDate=${endDate}`);
  }
  if (featureId) {
    queryParameters.push(`featureId=${featureId}`);
  }
  if (trailId) {
    queryParameters.push(`trailId=${trailId}`);
  }
  if (queryParameters && queryParameters.length) {
    url += `?${queryParameters.join('&')}`;
  }
  return axios.get(url)
    .then((response) => response.data)
    .catch((error) => {
      console.log(error);
    });
};

const formResponseFunctions = {
  getFormResponses,
  getFormResponsesExport,
  getFormResponse,
  getFormResponseExport,
  getCsv,
  orchestrateUpload,
  deleteFormResponse,
  getFormResponsesCount,
};

export default formResponseFunctions;
